import { isAfter, isBefore, isSameDay } from 'date-fns'
import React, { useState } from 'react'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Grid } from 'shared/components/Base/Grid'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { DateRangeValueType } from 'shared/components/DateRangeSelector/DateRangeSelector'
import { Text } from 'shared/components/Text/Text'
import { formatDate } from 'shared/helpers/formatters'
import { toSentenceCase } from 'shared/helpers/utils'
import { useSafetyEvents } from 'shared/hooks/query/useSafetyEvents'
import { SafetyEvent } from 'shared/models/compliance'
import { midGray, primaryBlack } from 'shared/theme/colors'
import styled from 'styled-components'

import { SafetyEventsFiltersRow } from '../SafetyEventFilters/SafetyEventFiltersRow'
import { SafetyEventsFilter } from '../SafetyEventFilters/SafetyEventsFilter'
import {
  SafetyEventFiltersTypes,
  SelectedSafetyEventFiltersValuesType,
} from '../SafetyEventFilters/SafetyEventsFilters.constants'
import { SafetyToolEventsList } from './SafetyToolEventsList'

interface SafetyToolUserSafetyScoresProps {
  userId: string
}

export function SafetyToolUserSafetyScores({ userId }: SafetyToolUserSafetyScoresProps) {
  const { safetyEvents } = useSafetyEvents(userId, { enabled: !!userId })

  const [affectedScoreOnly, setAffectedScoreOnly] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState<Record<SafetyEventFiltersTypes, boolean>>({
    [SafetyEventFiltersTypes.DATE]: false,
    [SafetyEventFiltersTypes.CATEGORY]: false,
  })
  const [currentlyActiveFilter, setCurrentlyActiveFilter] = useState<SafetyEventFiltersTypes | undefined>(
    undefined
  )
  const [selectedFiltersValues, setSelectedFiltersValues] = useState<SelectedSafetyEventFiltersValuesType>({
    [SafetyEventFiltersTypes.CATEGORY]: undefined,
  })
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState<DateRangeValueType>([
    undefined,
    undefined,
  ])
  const initializeAllFilters = () => {
    setSelectedFiltersValues({
      [SafetyEventFiltersTypes.CATEGORY]: undefined,
    })
    setSelectedDateRangeValue([undefined, undefined])
  }

  const dateRangeFilterFn = (x: SafetyEvent) => {
    if (!selectedFilters[SafetyEventFiltersTypes.DATE]) {
      return true
    }
    if (selectedDateRangeValue[0] === 'ALL' && selectedDateRangeValue[1] === 'ALL') {
      return true
    }
    if (
      selectedDateRangeValue[0] &&
      selectedDateRangeValue[1] &&
      selectedDateRangeValue[0] !== 'ALL' &&
      selectedDateRangeValue[1] !== 'ALL'
    ) {
      return (
        (isAfter(new Date(x.eventEnd), selectedDateRangeValue[0]) ||
          isSameDay(new Date(x.eventEnd), selectedDateRangeValue[0])) && // is on or after range start
        (isBefore(new Date(x.eventEnd), selectedDateRangeValue[1]) ||
          isSameDay(new Date(x.eventEnd), selectedDateRangeValue[1]))
      ) // is on or before range end
    }

    return false
  }

  const filteredEventsRows = safetyEvents
    ?.map((x, idx) => ({ ...x, id: x.userId + idx }))
    ?.filter((x) => !affectedScoreOnly || !!x.remainingPoints)
    ?.filter(
      (x) =>
        !selectedFilters[SafetyEventFiltersTypes.CATEGORY] ||
        x.eventCategory === selectedFiltersValues[SafetyEventFiltersTypes.CATEGORY]
    )
    ?.filter((x) => dateRangeFilterFn(x))

  const eventsRows = filteredEventsRows ?? []

  return (
    <FlexBox isColumn gap="16px">
      <Grid alignItems="center" columns="auto 1fr auto" gap="40px">
        <FlexBox width="120px">
          <SafetyEventsFilter
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            selectedFiltersValues={selectedFiltersValues}
            setSelectedFiltersValues={setSelectedFiltersValues}
            selectedDateRangeValue={selectedDateRangeValue}
            setSelectedDateRangeValue={setSelectedDateRangeValue}
            currentlyActiveFilter={currentlyActiveFilter}
            setCurrentlyActiveFilter={setCurrentlyActiveFilter}
          />
        </FlexBox>

        <StyledCheckbox
          label={
            <Text variant="label2" fontColor={midGray}>
              Show only the factors affecting the score
            </Text>
          }
          value={affectedScoreOnly}
          onChange={setAffectedScoreOnly}
        />

        <Text variant="header4">{eventsRows?.length ?? 0} factors</Text>
      </Grid>

      <SafetyEventsFiltersRow
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        selectedFiltersValues={selectedFiltersValues}
        setSelectedFiltersValues={setSelectedFiltersValues}
        selectedDateRangeValue={selectedDateRangeValue}
        setSelectedDateRangeValue={setSelectedDateRangeValue}
        initializeAllFilters={initializeAllFilters}
      />

      <FlexBox>
        <SafetyToolEventsList
          defaultOrderBy="eventEnd"
          rows={eventsRows}
          columns={[
            {
              field: 'eventEnd',
              headerName: 'Date',
              width: 140,
              formatter: (e) => formatDate(e.eventEnd),
              fontVariant: 'label2',
              fontColor: midGray,
            },
            {
              field: 'eventDetails',
              headerName: 'Event',
              width: 460,
              fontVariant: 'header3',
              fontColor: primaryBlack,
            },
            {
              field: 'eventCategory',
              headerName: 'Category',
              width: 180,
              formatter: (e) =>
                toSentenceCase((e.eventCategory ?? '').replace(/_/g, ' ').toLowerCase()).replace('and', '&'),
              fontVariant: 'label2',
              fontColor: primaryBlack,
            },
            {
              field: 'remainingPoints',
              headerName: 'Points',
              width: 80,
              formatter: (e) =>
                // TODO: Verify originalPoints is always 20
                `${e.remainingPoints ? e.remainingPoints.toFixed(2) : 0} / ${
                  e.originalPoints ? Math.round(e.originalPoints) : 20
                }`,
              numeric: true,
              fontVariant: 'label2',
              fontColor: primaryBlack,
            },
          ]}
        />
      </FlexBox>
    </FlexBox>
  )
}

const StyledCheckbox = styled(Checkbox)`
  margin: -10px 0;
`
